import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { getMethod } from "../../services/ApiCallMethods";

export const getAdvisorClient = createAsyncThunk(
  "advisorClient/getAdvisorClient",
  async (
    {
      currentPage = 1,
      itemsPerPage = 10,
      advisorId,
      search = "",
      client_id = "",
      investment = "",
      investment_amount = "",
    },
    { rejectWithValue }
  ) => {
    try {
      const params = new URLSearchParams();
      params.append("page", currentPage);
      params.append("limit", itemsPerPage);
      if (search) params.append("search", search);
      if (client_id) params.append("client_id", client_id);
      if (investment) params.append("investment", investment);
      if (investment_amount)
        params.append("investment_amount", investment_amount);

      const url = `/advisor/advisorClientDetail/${advisorId}?${params.toString()}`;
      const response = await getMethod(url);
      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error fetching clients", error);
      return rejectWithValue(error?.response?.data || "Something Went Wrong");
    }
  }
);

export const getAdvisorClientForModal = createAsyncThunk(
  "advisorClientModal/getAdvisorClientForModal",
  async (
    {
      currentPage = 1,
      itemsPerPage = 10,
      advisorId,
      search = "",
      client_id = "",
      investment = "",
    },
    { rejectWithValue }
  ) => {
    try {
      const params = new URLSearchParams();
      params.append("page", currentPage);
      params.append("limit", itemsPerPage);
      if (search) params.append("search", search);
      if (client_id) params.append("client_id", client_id);
      const url = `/advisor/advisorClientDetail/${advisorId}?${params.toString()}`;
      const response = await getMethod(url);
      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue(error?.response?.data || "Something Went Wrong");
    }
  }
);

export const getAdvisorClientDocuments = createAsyncThunk(
  "advisorClient/getAdvisorClientDocuments",
  async (
    { clientId, advisorId, search, account, accountType, timeFrame },
    { rejectWithValue }
  ) => {
    try {
      const params = new URLSearchParams();

      if (search) params.append("search", search);
      if (account) params.append("account", account);
      if (accountType) params.append("account_type", accountType);
      if (timeFrame) params.append("timeframe", timeFrame);
      if (advisorId) params.append("advisorId", advisorId)
      const url = `/api/documents/advisorClientDocument/${clientId}?${params.toString()}`;
      const response = await getMethod(url);
      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error fetching clients", error);
      return rejectWithValue(error?.response?.data || "Something Went Wrong");
    }
  }
);

export const getAdvisorClientProperties = createAsyncThunk(
  "advisorClient/getAdvisorClientProperties",
  async (
    { currentPage, itemsPerPage, clientId, search },
    { rejectWithValue }
  ) => {
    try {
      const params = new URLSearchParams();
      params.append("page", currentPage);
      params.append("limit", itemsPerPage);
      params.append("search", search);
      const url = `/advisor/getClientProperties/${clientId}?${params.toString()}`;
      const response = await getMethod(url);
      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error fetching clients", error);
      return rejectWithValue(error?.response?.data || "Something Went Wrong");
    }
  }
);

export const getClientById = createAsyncThunk(
  "advisorClient/getClientById",
  async (clientId, { rejectWithValue }) => {
    try {
      const response = await getMethod(`/api/users/${clientId}`);
      const json = await response.json();
      return json.data || json;
    } catch (error) {
      console.error("No Advisor Found", error);
      return rejectWithValue(error.response?.data || "Advisor Not Found");
    }
  }
);

export const getAllRealEstateHoldings = createAsyncThunk(
  "advisorClient/getAllRealEstateHoldings",
  async (payload, { rejectWithValue }) => {
    try {
      let response;
      if (payload?.securityType) {
        response = await getMethod(
          `/profileSummary/getFullPortfolioByClientId/${payload?.clientId}?property_id=${payload?.propertyId}&security_type=${payload?.securityType}`
        );
      } else {
        let url = `/profileSummary/getFullPortfolioByClientId/${payload?.clientId}`;

        let queryParams = [];

        if (payload?.advisorId) {
          queryParams.push(`advisorId=${payload.advisorId}`);
        }

        if (payload?.filters?.securityId) {
          queryParams.push(`productId=${payload?.filters?.securityId}`);
        }

        if (payload?.filters?.accountId) {
          queryParams.push(`accountId=${payload?.filters?.accountId}`);
        }

        if (payload?.filters?.investmentType) {
          queryParams.push(`investments=${payload?.filters?.investmentType}`);
        }

        if (payload?.selectedAccount) {
          queryParams.push(`ownerEntityId=${payload.selectedAccount}`);
        }

        if (queryParams.length > 0) {
          url += `?${queryParams.join("&")}`;
        }

        response = await getMethod(url);

      }
      const json = await response.json();
      return json.data || json;
    } catch (error) {
      console.error("No Advisor Found", error);
      return rejectWithValue(error.response?.data || "Advisor Not Found");
    }
  }
);

export const getAccountPortfolio = createAsyncThunk(
  "advisorClient/getAccountPortfolio",
  async ({ accountId, filters = {} }, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      params.append("accountId", accountId);
      if (filters.securityId)     params.append("productId",   filters.securityId);
      if (filters.investmentType) params.append("investments", filters.investmentType);
      const url = `/profileSummary/getPortfolioByAccount?${params.toString()}`;
      const response = await getMethod(url);
      const json = await response.json();
      return json.data || json;
    } catch (error) {
      console.error("Error fetching account portfolio", error);
      return rejectWithValue(error?.response?.data || "Failed to load account portfolio");
    }
  }
);

export const getAllRealEstateHoldingsWithAdvisorId = createAsyncThunk(
  "advisorClient/getAllRealEstateHoldingsWithAdvisorId",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await getMethod(
        `/profileSummary/getFullPortfolioByClientAndAdvisor/${payload.clientId}/${payload.advisorId}`
      );
      const json = await response.json();

      return json.data || json;
    } catch (error) {
      console.error("No Advisor Found", error);
      return rejectWithValue(error.response?.data || "Advisor Not Found");
    }
  }
);

export const getAllRealEstateHoldingsScreen2 = createAsyncThunk(
  "advisorClient/getAllRealEstateHoldingsScreen2",
  async ({ id, advisorId, clientId, hasChildren, type, rootId, categoryId, accountId }, { rejectWithValue }) => {
    try {
      let response;
      if (accountId) {
        if (id && type === "asset") {
          response = await getMethod(`/profileSummary/getPortfolioByAccount?accountId=${accountId}&security_type=${rootId}&category_id=${categoryId}&asset_id=${id}`);
        } else if (id && hasChildren && type === "category") {
          response = await getMethod(`/profileSummary/getPortfolioByAccount?accountId=${accountId}&security_type=${rootId}&category_id=${id}`);
        } else if (id && hasChildren) {
          response = await getMethod(`/profileSummary/getPortfolioByAccount?accountId=${accountId}&security_type=${id}`);
        } else {
          response = await getMethod(`/profileSummary/getInvestmentPortfolioByInvestmentId/${clientId}/${id}?accountId=${accountId}`);
        }
      } else {
        if (id && type === "asset") {
          response = await getMethod(`/profileSummary/getFullPortfolioByClientId/${clientId}?security_type=${rootId}&category_id=${categoryId}&asset_id=${id}&advisorId=${advisorId}`);
        } else if (id && hasChildren && type === "category") {
          response = await getMethod(`/profileSummary/getFullPortfolioByClientId/${clientId}?security_type=${rootId}&category_id=${id}&advisorId=${advisorId}`);
        } else if (id && hasChildren) {
          response = await getMethod(`/profileSummary/getFullPortfolioByClientId/${clientId}?security_type=${id}&advisorId=${advisorId}`);
        } else {
          response = await getMethod(`/profileSummary/getInvestmentPortfolioByInvestmentId/${clientId}/${id}`);
        }
      }

      const json = await response.json();
      return json.data || json;
    } catch (error) {
      console.error("No Advisor Found", error);
      return rejectWithValue(error.response?.data || "Advisor Not Found");
    }
  }
);
export const getRealEstateDocs = createAsyncThunk(
  "advisorClient/getRealEstateDocs",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getMethod(`/api/getRealEstateDocs`);
      const json = await response.json();
      return json.data || json;
    } catch (error) {
      console.error("No Advisor Found", error);
      return rejectWithValue(error.response?.data || "Advisor Not Found");
    }
  }
);
export const getInvestmentDocs = createAsyncThunk(
  "advisorClient/getInvestmentDocs",
  async ({ clientsId, name }, { rejectWithValue }) => {
    try {
      const url = `/api/client/${clientsId}/documents?accountType=${name}`;
      const response = await getMethod(url);
      const json = await response.json();
      return json.data || json;
    } catch (error) {
      console.error("No Investment Documents Found", error);
      return rejectWithValue(error.response?.data || "No Investment Documents Found");
    }
  }
);

export const getRealEstate2Docs = createAsyncThunk(
  "advisorClient/getRealEstate2Docs",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getMethod(`/api/getRealEstateDocs`);
      const json = await response.json();
      return json.data || json;
    } catch (error) {
      console.error("No Advisor Found", error);
      return rejectWithValue(error.response?.data || "Advisor Not Found");
    }
  }
);

const advClientSlice = createSlice({
  name: "advisorClient",
  initialState: {
    advClients: [],
    modalClients: [],
    loading: null,
    error: null,
    success: false,
    advClientDocument: [],
    properties: [],
    pagination: {},
    clientById: {},
    docPagination: {},
    propertiesPagination: {},
    realEstateError: null,
    realEstateLoading: false,
    realEstateHoldings: {},
    realEstateWithAdvisorIdHoldings: {},
    realEstateWithAdvisorIdLoading: false,
    realEstate2Holdings: {},
    realEstate2Loading: false,
    realEstateDocs: {},
    realEstate2Docs: {},
    investmentDocs: [],
    investmentDocsLoading: false,
    investmentDocsError: null,
  },
  reducers: {
    clearAdvClientState: (state) => {
      state.advClients = [];
      state.modalClients = [];
      state.loading = null;
      state.error = null;
      state.success = false;
      state.advClientDocument = [];
      state.properties = [];
      state.pagination = {};
      state.clientById = {};
      state.docPagination = {};
      state.propertiesPagination = {};
      state.realEstateError = null;
      state.realEstateLoading = false;
      state.realEstateHoldings = {};
      state.realEstateWithAdvisorIdHoldings = {};
      state.realEstateWithAdvisorIdLoading = false;
      state.realEstate2Holdings = {};
      state.realEstate2Loading = false;
      state.realEstateDocs = {};
      state.realEstate2Docs = {};
      state.investmentDocs = [];
      state.investmentDocsLoading = false;
      state.investmentDocsError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAdvisorClient.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(getAdvisorClient.fulfilled, (state, action) => {
        state.loading = false;
        state.advClients = action.payload.data;
        state.pagination = action.payload.pagination;
        state.success = true;
      })
      .addCase(getAdvisorClient.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.success = false;
      });
    builder
      .addCase(getAdvisorClientDocuments.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(getAdvisorClientDocuments.fulfilled, (state, action) => {
        state.loading = false;
        state.advClientDocument = action.payload.documents;
        state.docPagination = action.payload.pagination;
        state.success = true;
      })
      .addCase(getAdvisorClientDocuments.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;

        state.success = false;
      });
    builder
      .addCase(getAdvisorClientProperties.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(getAdvisorClientProperties.fulfilled, (state, action) => {
        state.loading = false;
        state.properties = action.payload.data;
        state.propertiesPagination = action.payload.pagination;
        state.success = true;
      })
      .addCase(getAdvisorClientProperties.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.success = false;
      });

    builder
      .addCase(getClientById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getClientById.fulfilled, (state, action) => {
        state.loading = false;
        state.clientById = action.payload;
        state.error = null;
      })
      .addCase(getClientById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
    builder
      .addCase(getAllRealEstateHoldings.pending, (state) => {
        state.realEstateLoading = true;
        state.error = null;
      })
      .addCase(getAllRealEstateHoldings.fulfilled, (state, action) => {
        state.realEstateLoading = false;
        state.realEstateHoldings = action.payload;
        state.error = null;
      })
      .addCase(getAllRealEstateHoldings.rejected, (state, action) => {
        state.realEstateLoading = false;
        state.error = action.payload;
      });
    builder
      .addCase(getAccountPortfolio.pending, (state) => {
        state.realEstateLoading = true;
        state.error = null;
      })
      .addCase(getAccountPortfolio.fulfilled, (state, action) => {
        state.realEstateLoading = false;
        state.realEstateHoldings = action.payload;
        state.error = null;
      })
      .addCase(getAccountPortfolio.rejected, (state, action) => {
        state.realEstateLoading = false;
        state.error = action.payload;
      });
    builder
      .addCase(getAllRealEstateHoldingsScreen2.pending, (state) => {
        state.realEstate2Loading = true;
        state.error = null;
      })
      .addCase(getAllRealEstateHoldingsScreen2.fulfilled, (state, action) => {
        state.realEstate2Loading = false;
        state.realEstate2Holdings = action.payload;
        state.error = null;
      })
      .addCase(getAllRealEstateHoldingsWithAdvisorId.pending, (state) => {
        state.realEstateWithAdvisorIdLoading = true;
        state.error = null;
      })
      .addCase(getAllRealEstateHoldingsWithAdvisorId.fulfilled, (state, action) => {
        state.realEstateWithAdvisorIdLoading = false;
        state.realEstateWithAdvisorIdHoldings = action.payload;
        state.error = null;
      })
      .addCase(getAllRealEstateHoldingsWithAdvisorId.rejected, (state, action) => {
        state.realEstateWithAdvisorIdLoading = false;
        state.error = action.payload;
      })
      .addCase(getAllRealEstateHoldingsScreen2.rejected, (state, action) => {
        state.realEstate2Loading = false;
        state.error = action.payload;
      });
    builder
      .addCase(getRealEstateDocs.pending, (state) => {
        state.getRealEstateDocsLoading = true;
        state.error = null;
      })
      .addCase(getRealEstateDocs.fulfilled, (state, action) => {
        state.getRealEstateDocsLoading = false;
        state.realEstateDocs = action.payload;
        state.error = null;
      })
      .addCase(getRealEstateDocs.rejected, (state, action) => {
        state.getRealEstateDocsLoading = false;
        state.error = action.payload;
      });
    builder
      .addCase(getRealEstate2Docs.pending, (state) => {
        state.getRealEstateDocsLoading = true;
        state.error = null;
      })
      .addCase(getRealEstate2Docs.fulfilled, (state, action) => {
        state.getRealEstateDocsLoading = false;
        state.realEstate2Docs = action.payload;
        state.error = null;
      })
      .addCase(getRealEstate2Docs.rejected, (state, action) => {
        state.getRealEstateDocsLoading = false;
        state.error = action.payload;
      });
    builder
      // getInvestmentDocs
      .addCase(getInvestmentDocs.pending, (state) => {
        state.investmentDocsLoading = true;
        state.investmentDocsError = null;
      })
      .addCase(getInvestmentDocs.fulfilled, (state, action) => {
        state.investmentDocsLoading = false;
        state.investmentDocs = action.payload || [];
      })
      .addCase(getInvestmentDocs.rejected, (state, action) => {
        state.investmentDocsLoading = false;
        state.investmentDocsError = action.payload || "Failed to fetch investment documents";
      });
    builder
      .addCase(getAdvisorClientForModal.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(getAdvisorClientForModal.fulfilled, (state, action) => {
        state.loading = false;
        state.modalClients = action.payload.data || [];
        state.success = true;
      })
      .addCase(getAdvisorClientForModal.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.success = false;
      });
  },
});

export const { clearAdvClientState } = advClientSlice.actions;
export default advClientSlice.reducer;
